import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { useApi } from "@/lib/api";

/**
 * Demand map for /admin/creators/:id (Tracked searches tab).
 * Themes of real searches behind what clients, brands and learners ask,
 * from autocomplete, People also ask, related searches and the searches
 * ChatGPT and Gemini run before answering (fan-out). A theme can be added
 * to the tracked set.
 */

const AUDIENCE_LABELS = {
    everyday: "Everyday ask",
    client: "Client",
    brand: "Brand",
    learner: "Learner",
};
const PATTERN_LABELS = {
    story: "Story and constraints",
    need: "Specific need",
    budget: "Price or schedule",
    trust: "Trust",
    vetting: "Vetting",
    compare: "Comparison",
    learn: "How-to",
    seasonal: "Seasonal",
};
const SOURCE_LABELS = {
    fanout: "AI searches",
    autocomplete: "autocomplete",
    paa: "people also ask",
    related: "related",
};
const STAGE_LABELS = {
    seeding: "Writing seed questions...",
    searching: "Searching Google and asking ChatGPT and Gemini...",
    grouping: "Grouping searches into themes...",
};

export default function DemandMapPanel({ creatorId, onTracked }) {
    const { request } = useApi();
    const [map, setMap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [open, setOpen] = useState(null);
    const [showSignals, setShowSignals] = useState(false);
    const [goals, setGoals] = useState(null);
    const [pages, setPages] = useState([]);
    const [pagesRunning, setPagesRunning] = useState(false);

    const load = useCallback(async () => {
        try {
            const [res, ap] = await Promise.all([
                request(`/admin/creators/${creatorId}/demand-map`),
                request(`/admin/creators/${creatorId}/answer-pages`),
            ]);
            setMap(res?.map || null);
            setGoals(res?.goals || null);
            setPages(ap?.pages || []);
            setPagesRunning(Boolean(ap?.running));
        } catch (err) {
            toast.error(err?.message || "Could not load the demand map.");
        } finally {
            setLoading(false);
        }
    }, [request, creatorId]);

    useEffect(() => {
        void load();
    }, [load]);

    // Poll while a build runs.
    useEffect(() => {
        if (map?.status !== "running" && !pagesRunning) return undefined;
        const t = setTimeout(() => void load(), 5000);
        return () => clearTimeout(t);
    }, [map, pagesRunning, load]);

    const buildPages = async () => {
        try {
            await request(`/admin/creators/${creatorId}/answer-pages`, { method: "POST" });
            setPagesRunning(true);
        } catch (err) {
            toast.error(err?.message || "Could not start the answer pages.");
        }
    };

    const build = async () => {
        setStarting(true);
        try {
            await request(`/admin/creators/${creatorId}/demand-map`, { method: "POST" });
            await load();
        } catch (err) {
            toast.error(err?.message || "Could not start the demand map.");
        } finally {
            setStarting(false);
        }
    };

    const track = async (i) => {
        try {
            const res = await request(`/admin/creators/${creatorId}/demand-map/track`, {
                method: "POST",
                body: { map_id: map.id, theme_index: i },
            });
            toast.success(res?.already ? "Already tracked." : "Added to tracked searches.");
            await load();
            onTracked?.();
        } catch (err) {
            toast.error(err?.message || "Could not add the question.");
        }
    };

    const running = map?.status === "running";
    const themes = map?.themes || [];
    const tracked = new Set(map?.tracked_themes || []);
    const fanout = (map?.signals || []).filter((s) => s.source === "fanout");

    return (
        <section
            data-testid="admin-demand-map"
            className="border border-black/10 rounded-2xl bg-white p-5"
        >
            <header className="flex items-start justify-between gap-3 mb-3">
                <div>
                    <div className="samoony-label">Demand map</div>
                    <p className="text-sm text-black/60 mt-1">
                        What people, clients and brands actually search, including the
                        searches AI engines run before they answer.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="admin-demand-map-build"
                    onClick={build}
                    disabled={starting || running}
                    className="samoony-pill samoony-pill-primary disabled:opacity-30 shrink-0"
                >
                    {running ? "Building..." : map ? "Rebuild" : "Build demand map"}
                </button>
            </header>

            {!loading && goals && (
                <div
                    data-testid="admin-demand-goals"
                    className="text-sm text-black/70 mb-3"
                >
                    <span className="samoony-label">Detected</span>{" "}
                    {goals.book ? "Sells a service" : "No bookable service found"}
                    {goals.sensitive ? " · Sensitive niche (no product claims)" : ""}
                    {[...(goals.evidence?.book || []), ...(goals.evidence?.sensitive || [])]
                        .length > 0 && (
                        <div className="font-mono text-[11px] text-black/50 mt-1">
                            {[...(goals.evidence?.book || []), ...(goals.evidence?.sensitive || [])]
                                .slice(0, 4)
                                .join(" · ")}
                        </div>
                    )}
                </div>
            )}
            {loading && <div className="text-sm text-black/50">Loading...</div>}
            {!loading && !map && (
                <div className="text-sm text-black/60">No demand map yet.</div>
            )}
            {running && (
                <div data-testid="admin-demand-map-stage" className="text-sm text-black/60">
                    {STAGE_LABELS[map.stage] || "Working..."}
                </div>
            )}
            {map?.status === "failed" && (
                <div className="text-sm text-signal">Build failed: {map.error}</div>
            )}

            {map?.status === "done" && (
                <>
                    <div className="font-mono text-[11px] text-black/50 mb-3">
                        Built {new Date(map.finished_at).toLocaleString("en-US", {
                            timeZone: "America/Edmonton",
                        })}{" "}
                        · {(map.signals || []).length} searches · {fanout.length} from AI engines
                    </div>
                    {themes.length === 0 && (
                        <div className="text-sm text-black/60">No themes found.</div>
                    )}
                    <ul className="divide-y divide-black/10 border-y border-black/10">
                        {themes.map((t, i) => (
                            <li key={i} data-testid={`admin-demand-theme-${i}`} className="py-3">
                                <div className="flex items-start justify-between gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setOpen(open === i ? null : i)}
                                        className="text-left flex-1"
                                    >
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="samoony-chip !py-0.5 text-[10px] uppercase tracking-widest">
                                                {AUDIENCE_LABELS[t.audience] || t.audience}
                                            </span>
                                            {t.pattern && (
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-black/50">
                                                    {PATTERN_LABELS[t.pattern] || t.pattern}
                                                </span>
                                            )}
                                            <span className="font-display text-ink">{t.name}</span>
                                        </div>
                                        <div className="text-sm text-black/70 mt-1">{t.content}</div>
                                        <div className="font-mono text-[11px] text-black/50 mt-1">
                                            {Object.entries(t.sources || {})
                                                .map(([k, v]) => `${v} ${SOURCE_LABELS[k] || k}`)
                                                .join(" · ") || "no matched searches"}
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`admin-demand-track-${i}`}
                                        onClick={() => track(i)}
                                        disabled={tracked.has(i)}
                                        className="samoony-pill samoony-pill-ghost shrink-0 disabled:opacity-40"
                                    >
                                        {tracked.has(i) ? "Tracked" : "Track"}
                                    </button>
                                </div>
                                {open === i && (
                                    <div className="mt-2 pl-1">
                                        <div className="text-xs text-black/70">
                                            Tracked question: <span className="text-ink">{t.question}</span>
                                        </div>
                                        <ul className="mt-1 font-mono text-[11px] text-black/70 space-y-0.5">
                                            {(t.queries || []).map((q, j) => (
                                                <li key={j}>{q}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                    <button
                        type="button"
                        onClick={() => setShowSignals((v) => !v)}
                        className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-black/60"
                    >
                        {showSignals ? "Hide AI searches" : `Show AI searches (${fanout.length})`}
                    </button>
                    {showSignals && (
                        <ul
                            data-testid="admin-demand-fanout"
                            className="mt-2 font-mono text-[11px] text-black/70 space-y-0.5"
                        >
                            {fanout.map((s, i) => (
                                <li key={i}>
                                    {s.query}
                                    <span className="text-black/40">
                                        {" "}
                                        · {s.engine || "engine"} · from "{s.seed}"
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div data-testid="admin-answer-pages" className="mt-5">
                        <div className="flex items-center justify-between gap-3">
                            <div className="samoony-label">Answer pages</div>
                            <button
                                type="button"
                                data-testid="admin-answer-pages-build"
                                onClick={buildPages}
                                disabled={pagesRunning}
                                className="samoony-pill samoony-pill-ghost shrink-0 disabled:opacity-40"
                            >
                                {pagesRunning ? "Writing..." : "Rebuild pages"}
                            </button>
                        </div>
                        <p className="text-sm text-black/60 mt-1">
                            Written from the creator's posts for everyday themes. A page goes
                            live with 5 or more items quoted from real posts.
                        </p>
                        {pages.length === 0 && !pagesRunning && (
                            <div className="text-sm text-black/60 mt-2">No answer pages yet.</div>
                        )}
                        <ul className="mt-2 divide-y divide-black/10 border-y border-black/10">
                            {pages.map((p) => (
                                <li key={p.id} className="py-2 text-sm">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="samoony-chip !py-0.5 text-[10px] uppercase tracking-widest">
                                            {p.status === "published" ? "Live" : "Held"}
                                        </span>
                                        {p.url ? (
                                            <a href={p.url} target="_blank" rel="noreferrer" className="text-ink underline">
                                                {p.title}
                                            </a>
                                        ) : (
                                            <span className="text-ink">{p.theme_name}</span>
                                        )}
                                    </div>
                                    <div className="font-mono text-[11px] text-black/50 mt-1">
                                        {p.status === "published" ? `${p.item_count} items` : ""}
                                        {p.hold_reason
                                            ? `${p.status === "published" ? " · last check: " : ""}${p.hold_reason}`
                                            : ""}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            )}
        </section>
    );
}
